/**
 * duelComments.ts
 * Local comment engine for the Duel Replay — no API needed.
 * English-language templates: motivating, funny, with comparative context.
 */
import { Activity } from '../types/activity';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDur(secs: number): string {
  const m = Math.round(secs / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h}h` : `${h}h ${r}min`;
}

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function fill(tpl: string, vars: Record<string, string | number>): string {
  return tpl.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ''));
}

// ─── Speaker context: own weekly stats ───────────────────────────────────────

interface SpeakerCtx {
  totalKm: number;
  totalElev: number;
  totalPoints: number;
  totalDurSecs: number;
  workoutCount: number;
  maxDurSecs: number;
  sportCount: number;
}

function computeCtx(acts: Activity[]): SpeakerCtx {
  const sportTypes = new Set(acts.map(a => a.sport_type));
  return {
    totalKm:      acts.reduce((s, a) => s + (a.distance_meters ?? 0) / 1000, 0),
    totalElev:    acts.reduce((s, a) => s + (a.elevation_gain_m ?? 0), 0),
    totalPoints:  acts.reduce((s, a) => s + Number(a.total_points ?? 0), 0),
    totalDurSecs: acts.reduce((s, a) => s + (a.duration_seconds ?? 0), 0),
    workoutCount: acts.length,
    maxDurSecs:   acts.reduce((m, a) => Math.max(m, a.duration_seconds ?? 0), 0),
    sportCount:   sportTypes.size,
  };
}

// ─── Generic ──────────────────────────────────────────────────────────────────

const GENERIC: string[] = [
  "{name} means business!",
  "That's what commitment looks like.",
  "{dur} of pure focus — well done, {name}!",
  "Impressive. Simply impressive.",
  "This is how it's done. {name} leads the way.",
  "{name} turns it up a notch!",
  "The results speak for themselves.",
  "Full commitment from {name}.",
  "That was more than just checking a box.",
  "{name} is playing to win.",
  "Thanks, {name}. Really needed that motivation. Or not.",
  "That wasn't necessary, {name}. Was it?",
  "I thought my training was solid...",
  "{name} makes it look effortless.",
  "Impressed. And slightly nervous.",
  "Please stop. Just kidding. Keep going.",
  "Does {name} even sleep?",
  "This workout will haunt me tonight.",
  "Thanks a lot, {name}. Now I can't sleep.",
  "I saw it, I registered it, I'm suffering.",
  "All good. Totally fine. Completely relaxed. Yep.",
  "Nice of {name} to raise the bar like that.",
  "That should be illegal. Morally speaking.",
  "My training plan calls this 'overachievement'.",
  "Need to rethink my entire week now.",
  "That feels personal, {name}.",
  "What does {name} do in their free time? More sports.",
  "I trained too. Just... differently.",
  "Congrats, {name}. You're ruining all my excuses.",
  "{dur} well spent.",
  "No days off. {name} lives by that.",
  "Workout complete. {name} delivers.",
  "{name} banks the points.",
  "Every session counts. {name} knows that.",
  "Methodical. Consistent. Dangerous.",
  "No compromises.",
  "{name} doesn't care if it's Monday or Sunday.",
  "Training done. Next mission?",
  "{name}'s consistency is remarkable.",
  "Not a single day wasted.",
  "Session by session, {name} builds the lead.",
  "The scoreboard doesn't lie.",
  "{name} is the definition of disciplined.",
  "Zero excuses. Maximum output.",
  "Another brick in the wall.",
  "Steady. Strong. Dangerous.",
  "Solid. Very solid.",
  "That was a statement.",
  "{name} is in the zone.",
  "Well executed, {name}.",
  "No luck involved. Just hard work.",
  "The numbers don't lie.",
  "{name} delivered again.",
  "Reliable as clockwork.",
  "Nice work, {name}. But I'm coming.",
  "Noted. With respect.",
  "{name} shows how it's done.",
  "This week is going well for {name}.",
  "Just do it. {name} does it.",
  "Point by point, {name} takes the week.",
  "{name} is on the right track.",
  "Hard. Persistent. Consistent.",
  "Better than nothing? No — much better than nothing.",
  "{name} leaves nothing to chance.",
  "That's how you secure your points.",
  "{dur} — and every minute counted.",
  "The dedication is paying off.",
  "Always here, always active — that's {name}.",
  "Full marks for the effort.",
  "Who trains like this needs no excuses.",
  "The result doesn't come from nowhere.",
  "{name} is on another level today.",
  "The training is paying off.",
  "Chapeau, {name}!",
];

// ─── Short duration < 20 min ──────────────────────────────────────────────────

const DUR_SHORT: string[] = [
  "{dur}? Short and sharp. That counts.",
  "In and out fast — {name} is efficient.",
  "Short but not quiet.",
  "The compact session. Underrated but effective.",
  "{dur} — sometimes that's all it takes.",
  "Flash training from {name}. Points are there.",
  "More done in {dur} than others manage in an hour.",
  "Quality over quantity. {name} proves it.",
  "{dur}: a surgical strike on the leaderboard.",
  "Who says it has to be long? Not {name}.",
  "Hard to be more efficient. {dur} is enough.",
  "{dur} — alarm rang, {name} trained.",
  "Flash session from {name}. Result: points.",
  "Short session, full effect. Don't underestimate it.",
  "In {dur} {name} gets more done than I did in my last hour.",
];

// ─── Long duration > 75 min ───────────────────────────────────────────────────

const DUR_LONG: string[] = [
  "{dur}! {name} means it.",
  "Over an hour — {name} sees it through.",
  "{dur} straight. That's pure endurance.",
  "Who trains this long? {name}. Obviously {name}.",
  "That's not training. That's a project.",
  "{dur} — my weekend is shorter.",
  "Respect for this endurance. {dur} without stopping.",
  "After an hour most people start reconsidering. {name} keeps training.",
  "{dur}. The word 'exhaustion' is negotiable for {name}.",
  "That was a marathon of discipline. {dur}.",
  "Enduring to the limit — and beyond.",
  "{name} invested {dur} in themselves today.",
  "Long sessions are {name}'s specialty.",
  "Who trains for {dur} needs no excuses.",
  "An epic session from {name}. {dur}.",
];

// ─── Very long duration > 2h ──────────────────────────────────────────────────

const DUR_VERY_LONG: string[] = [
  "{dur}?! I need a break just reading that.",
  "Has {name} even come home yet?",
  "Two hours plus — that's not sport, that's a lifestyle.",
  "{dur} straight. Legendary.",
  "{name} trained for longer today than I slept.",
  "I think {name} forgot to stop.",
  "{dur} — that deserves not just points, but recognition.",
  "Ultra performance. {dur} non-stop.",
  "That wasn't a training session. That was an expedition.",
  "After {dur} of training, lunch has to wait.",
];

// ─── With distance ────────────────────────────────────────────────────────────

const WITH_KM: string[] = [
  "{km} in one workout — legs on overtime today.",
  "Nice {km} on the clock, {name}.",
  "{km} covered. That will show up in the standings.",
  "Those {km} will still be felt in the weekly result.",
  "{km} — and {name} barely looked like sweating.",
  "Collecting kilometers like stamps. {km} today.",
  "{km} underfoot. Respect.",
  "With {km}, {name} makes a clear statement.",
  "The distance: {km}. The message: clear.",
  "{name} clocked {km} today. Consistent.",
  "{km} in {dur} — that's solid work.",
  "{km} and not a single meter gifted.",
  "GPS confirms: {km}. Well done.",
  "{km} in the legs. The rest of the week takes note.",
  "Step by step — {km} of them today.",
  "{km} — more than just a casual outing.",
  "The distance: {km}. The result: earned.",
  "{km} in one go — respect.",
  "The {km} speak for themselves.",
  "{name} put down {km} today. Noted.",
];

// ─── Distance beats my weekly total ──────────────────────────────────────────

const KM_BEATS_OPP: string[] = [
  "{km} in a single session — I've only done {myKm} all week!",
  "More kilometers in one workout than I've run all week. {km} vs. my {myKm}!",
  "My weekly distance: {myKm}. {name}'s workout today: {km}. That says it all.",
  "More distance in one session than I've covered in 7 days. Thanks, {name}.",
  "{km} at once? I'm only at {myKm} for the entire week.",
  "You've packed my whole week into one workout: {km} vs. {myKm}!",
  "My weekly total vs. your single session. {myKm} vs. {km}. I'm embarrassed.",
  "{km} today — more than my entire week's distance. I need to step it up.",
  "One workout, more kilometers than my whole week: {km} vs. {myKm}.",
  "Impressive: {km} in one session while I'm sitting at {myKm} for the week.",
  "Your single session tops my weekly mileage. {km} > {myKm}. Chapeau.",
  "{km} in one shot — more than my complete weekly distance ({myKm}).",
  "I'm grinding through {myKm} for the whole week — {name} does {km} in a day.",
  "Weekly distance: {myKm}. {name}'s single workout: {km}. I feel judged.",
  "That {km} just lapped my weekly total of {myKm}.",
];

// ─── Distance > 60% of my weekly total ───────────────────────────────────────

const KM_NEAR_OPP: string[] = [
  "Almost as many kilometers as I've done all week — {km} in one workout!",
  "{km} today — that's nearly half my weekly distance of {myKm}.",
  "More than half my weekly mileage in a single session. {km} is no joke!",
  "{km} at once — dangerously close to my weekly total of {myKm}!",
  "Already over half my week's distance: {km} vs. {myKm} total.",
  "Almost my full weekly mileage in one run! {km} shows what {name} is made of.",
  "Keep this up and {name} will easily beat my {myKm} weekly total.",
  "{km} in one session — a big chunk of my weekly {myKm}.",
  "Creeping up on my weekly distance with {km}. My {myKm} total is under threat.",
  "{km} from {name} getting dangerously close to my weekly {myKm}.",
];

// ─── With elevation ───────────────────────────────────────────────────────────

const WITH_ELEV: string[] = [
  "{elev} of elevation — {name} seeks the challenge upward.",
  "Climbing mode: {name} gains {elev}.",
  "{elev} of vertical — respectable ascent.",
  "Who willingly goes uphill? {name}. For {elev}.",
  "{elev} of elevation — legs burning, points incoming.",
  "Up and up: {elev}.",
  "{elev} earned. Every meter counts.",
  "The profile says it all: {elev} of climbing.",
  "Mountains aren't obstacles for {name}, they're points.",
  "{elev} — that was no flat route.",
  "Elevation: {elev}. Morale: unbroken.",
  "Going up is the direction. {elev} proves it.",
  "{name} climbed {elev} of elevation. Respect.",
  "Sweat and views: {elev} of evidence.",
  "{elev} in the legs — {name} always goes uphill.",
];

// ─── Elevation beats my weekly total ─────────────────────────────────────────

const ELEV_BEATS_OPP: string[] = [
  "More elevation in this one workout than I've climbed all week! {elev} vs. my {myElev}!",
  "That {elev} in one workout exceeds my entire week's climbing ({myElev}). Unfair.",
  "{elev} in a single session — more than I've climbed all week ({myElev} total)!",
  "My weekly elevation: {myElev}. {name}'s workout today: {elev}. You're the mountain.",
  "I've climbed {myElev} this week — {name} does that in a single run!",
  "{elev} at once — more than my entire week's elevation profile ({myElev}). Respect.",
  "My whole week in one run: my {myElev} weekly elevation vs. {name}'s {elev} today.",
  "Vertical game on another level: {elev} today vs. my {myElev} for the week.",
  "More elevation than I've done all week. Bravo, {name}!",
  "{elev} — my weekly elevation record is {myElev}. You just crushed it today.",
  "More vertical meters in one workout than I've done all week combined!",
  "{name} climbs {elev} — I managed {myElev} total this week.",
  "Nice one, {name}: more elevation than my whole week! ({elev} vs. {myElev})",
  "Vertical dominance: {elev} in one session, I'm at {myElev} for the week.",
  "Your single effort ({elev}) beats my weekly record ({myElev}). Impressive.",
];

// ─── Elevation > 50% of my weekly total ──────────────────────────────────────

const ELEV_NEAR_OPP: string[] = [
  "Almost my entire week's elevation in one workout! {elev} vs. {myElev}.",
  "{elev} — that's already over half my weekly climbing of {myElev}!",
  "More than half my weekly elevation in one session. {name} is applying pressure.",
  "{elev} at once is closing in on my weekly total of {myElev}.",
  "Almost as much elevation as I've done all week — {elev} vs. {myElev} total.",
  "{name} racks up {elev} today, eating into my {myElev} weekly elevation.",
  "More than half my weekly vertical in one workout. Strong!",
  "{elev} from {name} is dangerously close to my weekly total ({myElev}).",
  "The elevation profile is getting threatening: {elev} in one session, I'm at {myElev}.",
  "Nearly half my weekly elevation in one workout! {elev} is a statement.",
];

// ─── With heart rate ──────────────────────────────────────────────────────────

const WITH_HR: string[] = [
  "Avg heart rate at {hr} bpm — {name} was fully in it.",
  "{hr} bpm average says: that was no recovery run.",
  "The heart did its job: {hr} bpm.",
  "{hr} bpm — the body got the memo.",
  "Cardio confirmed: {hr} bpm. Full effort.",
  "{hr} bpm average — not a leisurely stroll.",
  "The heart knows no limits. {hr} bpm proves it.",
  "Heart check: {hr} bpm. All good. All in the red zone.",
  "{name} pushed at {hr} bpm. That counts.",
  "{hr} bpm average — I'd call that full throttle.",
  "Impressive cardiac work: {hr} bpm average.",
  "The pulse doesn't lie: {hr} bpm — fully committed.",
  "{hr} bpm — {name} was in the flow. Or in the pain. Both count.",
  "Heart rate: {hr} bpm. Seriousness: confirmed.",
  "Nothing held back at {hr} bpm average.",
];

// ─── High points > 70 ─────────────────────────────────────────────────────────

const PTS_HIGH: string[] = [
  "{pts} points! {name} goes on the attack.",
  "That was a {pts}-point workout. Impressive.",
  "{pts} points in one session — that stings.",
  "Not good, great: {pts} points.",
  "{name} rakes in a massive {pts} points.",
  "{pts} points — the leaderboard trembles.",
  "With {pts} points, {name} is writing history.",
  "Record territory: {pts} points from a single session.",
  "{pts} points — I'd rather not count those in my loss column.",
  "Point harvest: {pts}. The week belongs to {name}.",
  "{pts} points in one workout — who does that?",
  "The scoreboard lights up: {pts} points.",
  "{pts} points. Just {pts} points. Outrageously good.",
  "High score from {name}: {pts} points for this session.",
  "If points were money: {name} just earned {pts}.",
];

// ─── Low points < 25 ──────────────────────────────────────────────────────────

const PTS_LOW: string[] = [
  "{pts} points today — but showing up matters.",
  "Not the highest haul with {pts} points, but still better than skipping.",
  "{pts} points. Every single one counts.",
  "Easy day, {pts} points — but {name} keeps showing up.",
  "{pts} points isn't much, but it's honest work.",
  "Recovery day? {pts} points still land on the account.",
  "Sometimes {pts} points is enough to stay in the game. {name} gets it.",
  "Small score, big persistence. {pts} points.",
];

// ─── Points > 30% of my weekly total ─────────────────────────────────────────

const PTS_BEATS_OPP: string[] = [
  "{pts} points in one session — a significant chunk of my weekly {myPts}.",
  "Over 30% of my weekly points in one workout! {pts} vs. {myPts} total.",
  "{pts} points in one session — I've only got {myPts} for the whole week.",
  "Nearly a third of my weekly points in a single workout. Respect, {name}.",
  "{name} racks up {pts} here — a big bite of my {myPts} total.",
  "I'm at {myPts} for the whole week — {name} pulls {pts} in one session.",
  "{pts} points at once — gnawing at my {myPts}-point week.",
  "This {pts}-point session is a serious attack on my {myPts} weekly total.",
  "Weekly points comparison: me at {myPts}, {name} scoring {pts} in one go.",
  "That hurts: {pts} points for this workout, I'm at {myPts} total this week.",
];

// ─── Fast pace < 5:00 min/km ──────────────────────────────────────────────────

const PACE_FAST: string[] = [
  "{pace} — that's not jogging, that's racing.",
  "Pace: {pace}. {name} is not here to sightsee.",
  "{pace} average — {name} is seriously quick.",
  "Running at {pace}? That's a threat.",
  "The legs were flying at {pace} per km.",
  "{pace} per km — the watch doesn't lie.",
  "Sub-5 pace from {name}. That's elite territory.",
  "{pace} — my legs read that and gave up.",
  "Fast. Really fast. {pace} per km.",
  "That {pace} pace is making me reconsider my training.",
  "Speed mode activated: {pace} per km.",
  "At {pace}, {name} is not here to jog.",
  "Blazing {pace} — respect, {name}.",
  "{pace} average pace. Impressed and intimidated.",
  "Quick feet, big points: {pace} per km.",
];

// ─── Slow pace > 7:00 min/km ──────────────────────────────────────────────────

const PACE_SLOW: string[] = [
  "{pace} — easy does it. Recovery runs count too.",
  "Taking it steady at {pace}. Smart training.",
  "{pace} per km — this was about distance, not speed.",
  "Not every run needs to be fast. {pace} shows maturity.",
  "Active recovery mode: {pace}. Wise choice.",
  "Easy pace, real effort. {pace} from {name}.",
  "{pace} — sometimes slow is the secret weapon.",
  "Steady and controlled at {pace}. The base is building.",
  "Endurance run at {pace}. The long game.",
  "{pace} — the aerobic engine is being built.",
];

// ─── With PRs ─────────────────────────────────────────────────────────────────

const WITH_PR: string[] = [
  "{pr} personal record(s) in this session! {name} is breaking barriers.",
  "A new personal best! {pr} PR(s) set today.",
  "{pr} PR(s)! {name} pushes their own limits.",
  "Personal records today: {pr}. History is being written.",
  "{pr} new PR(s) — {name} is getting faster.",
  "Breaking personal records: {pr} of them.",
  "That wasn't just a workout, that was a {pr}-PR session.",
  "Personal best achieved! {pr} PR(s) in the books.",
  "{name} set {pr} personal record(s) today. That's progress.",
  "A new chapter: {pr} PR(s) for {name}.",
  "{pr} times faster than ever before.",
  "Records tumble! {pr} PR(s) from {name}.",
  "Personal records don't care about excuses. {pr} of them today.",
  "{pr} PR(s) — {name} just rewrote their story.",
  "That performance? Historic. {pr} PR(s).",
  "PRs on the board: {pr}. {name} is on fire.",
  "New personal best(s): {pr}. No looking back.",
  "{pr} personal record(s). The training is paying off.",
  "PR alert: {pr} of them from {name} today.",
  "Peak performance: {pr} PR(s) set.",
];

// ─── Sport: Running ───────────────────────────────────────────────────────────

const SPORT_RUN: string[] = [
  "{name} laces up. For {dur}.",
  "Running is both duty and joy for {name}.",
  "{dur} on the pavement — {name} does it again.",
  "The run is done. {name} checks the box.",
  "A run for the record books. {dur}.",
  "Out, run, back, points. That's how {name} operates.",
  "{name} runs. Weather is irrelevant.",
  "Done running! {dur}. Finished. Well done.",
  "Running clears the mind. {name} runs {dur}.",
  "{dur} on the road. {name} is in their element.",
  "Today's run: clean. The result: earned.",
  "Kilometer by kilometer — {name} builds the week.",
  "Run instead of rest. {name}'s motto.",
  "{name} turned the corner. All good.",
  "Run done. {dur}. No drama, just performance.",
  "A little more every day. Today: {dur} of running.",
  "The legs wanted to go. {name} let them.",
  "{name} is running into shape. {dur} of proof.",
  "Runners run. {name} runs {dur}.",
  "Miles logged, head cleared: {dur} of running.",
  "{name} and the road had a rendezvous today.",
  "Whether hills or flat — {name} runs {dur} through.",
  "Run session complete. {name} grows every week.",
  "{dur} running. The scoreboard was watching.",
  "{name} and their running shoes are a well-oiled machine.",
];

// ─── Sport: Cycling ───────────────────────────────────────────────────────────

const SPORT_RIDE: string[] = [
  "{name} let the wheels roll today.",
  "{dur} in the saddle — {name} turns the pedals.",
  "Uphill or downhill — main thing is pedaling.",
  "The route is done. {dur} with {name}.",
  "{name} cycles the week into shape.",
  "Clip in, {dur} of riding, clip out. Done.",
  "On two wheels for {dur} — {name} stays mobile.",
  "Full gas on the bike. {dur} long.",
  "The watts have spoken. So has {name}.",
  "{name} got in the saddle. For {dur}.",
  "Cycling is thinking with your legs. {name} thinks a lot.",
  "Pedal, collect points. {name} understands the game.",
  "{name} is in their element on the bike.",
  "{dur} cycling — head clear, legs full.",
  "A ride for the weekly tally. {dur}.",
  "The tour is done. {name} delivered.",
  "{name} goes by bike. Today for {dur}.",
  "Cadence. Speed. Points. {name} on the bike.",
  "On two wheels {name} goes further than I do.",
  "{name} and their bike got things done today.",
];

// ─── Sport: Swimming ──────────────────────────────────────────────────────────

const SPORT_SWIM: string[] = [
  "{name} hits the water — {dur} later, more performance comes out.",
  "In the pool, on the clock, in the standings. {name} swims.",
  "{dur} of laps — {name} doesn't mind the chlorine.",
  "Swimming: the underrated weapon. {name} knows this.",
  "The quiet sport with loud points. {dur} from {name}.",
  "Goggles on, points in. {name} in the pool.",
  "{name} spent {dur} in the water today. Voluntarily.",
  "Crawl, turn, crawl. {dur} long.",
  "In the water, {name} is a machine.",
  "Swim session: {dur}. Chlorine: mandatory. Points: real.",
  "Lap after lap for {dur} — the silence underwater shows no mercy.",
  "Wet but successful: {dur} of swimming.",
  "The pool has no bad weather. {name} uses that.",
  "{dur} swimming — the body thanks it. So does the scoreboard.",
  "{name} and the pool: {dur} of productive silence.",
];

// ─── Sport: Hiking ────────────────────────────────────────────────────────────

const SPORT_HIKE: string[] = [
  "{name} seeks the mountains. And finds them: {dur}.",
  "{dur} on the trail — no path too far for {name}.",
  "Hiking is more than a walk for {name}.",
  "Nature heard it: {name} was out for {dur}.",
  "Step by step up the mountain. {name} does this.",
  "{dur} on the hiking trail — recovery and training in one.",
  "Shaping the week through hiking. {name} gets it.",
  "{name} found the trail and enjoyed it for {dur}.",
  "Mountains aren't obstacles for {name}, they're the destination.",
  "{dur} hiking — body and soul both benefit.",
  "{name} is at home on the trail. Today for {dur}.",
  "Going high with {name}. {dur} on the way.",
  "Hiking for points — {name} has mastered this.",
  "{dur} in nature. Fresh air, fresh points.",
  "The trail doesn't wait. Neither does {name}. {dur}.",
];

// ─── Sport: Walking ───────────────────────────────────────────────────────────

const SPORT_WALK: string[] = [
  "{dur} walking — underestimated but effective.",
  "Step by step, point by point. {name} goes for it.",
  "{name} explored the world for {dur} today. On foot.",
  "Walking earns points. {name} knows this.",
  "{dur} in the fresh air — that counts.",
  "Sometimes walking is the smartest choice. {name} nails it.",
  "{name} walks their way. For {dur}.",
  "Active recovery at its best: {dur} walking.",
  "The walk that earns points: {dur} from {name}.",
  "Steady but purposeful. {dur} walking from {name}.",
];

// ─── Sport: Weight Training ───────────────────────────────────────────────────

const SPORT_WEIGHTS: string[] = [
  "{name} picks up the barbell. For {dur}.",
  "Strength training: {dur}. The muscles will say hello tomorrow.",
  "{dur} under the bar — {name} is building up.",
  "Iron doesn't lift itself. {name} helps for {dur}.",
  "The gym shook. {name} was there.",
  "{dur} pumping iron — that's what I call building.",
  "{name} invested today: in muscles and in points.",
  "Strength session logged. {dur}. {name} makes progress.",
  "Push, pull, repeat. {dur} from {name}.",
  "{dur} of strength training — the foundation work pays off.",
  "Bar up, bar down, points up. {dur}.",
  "{name} gave everything in the gym. {dur} is the proof.",
  "Muscle building and point building in one. {dur}.",
  "The weights got acquainted with {name} today.",
  "Strength is a form of endurance. {dur} of proof from {name}.",
];

// ─── Sport: Yoga ──────────────────────────────────────────────────────────────

const SPORT_YOGA: string[] = [
  "{dur} of yoga — don't underestimate it, the points are real.",
  "Calm and strength. {name} found both in {dur} of yoga.",
  "{name} stretches their way upward: {dur} of yoga.",
  "Yoga for {dur} — {name} finds balance.",
  "Mindful training: {dur} of yoga. More effective than it looks.",
  "Balance and control: {dur} of yoga. Underrated.",
  "Breathing and willpower: {dur} of yoga from {name}.",
  "Yoga isn't rest. {dur} of proof from {name}.",
  "{dur} on the mat — {name} takes this seriously.",
  "Flexible training plan. Flexible body too. {dur} of yoga.",
];

// ─── Sport: Virtual Ride ──────────────────────────────────────────────────────

const SPORT_VIRTUAL: string[] = [
  "Virtual Ride: {dur}. The avatar suffered.",
  "{name} pushes the pedals — indoors, {dur}.",
  "Bad weather outside? No problem. {dur} virtually.",
  "The trainer creaked. {name} pushed through. {dur}.",
  "Zwift or whatever — {dur} in the legs is real.",
  "Indoor cycling: everything real except the road. {dur}.",
  "{name} rides virtually for {dur}. The watts are very real.",
  "Rain outside, full gas inside. {dur} Virtual Ride.",
  "The equipment worked overtime. {dur} with {name}.",
  "Virtual route, real exhaustion. {dur} from {name}.",
];

// ─── Manual entry ─────────────────────────────────────────────────────────────

const SPORT_MANUAL: string[] = [
  "Manually logged. {dur}. I trust you, {name}.",
  "No GPS, but the conscience works. {dur} manual.",
  "{dur} without a tracker — {name} knows best what counts.",
  "Self-reported, self-earned. {dur}.",
  "The honor system in action. {dur} from {name}.",
  "No recording, but very real. {dur} logged.",
  "{name} has learned to trust themselves. {dur} manual.",
  "Manual means: the head was in it, even if the GPS wasn't.",
  "Well done, {name} — but manually entered activities don't earn points.",
  "Great effort, but no points for manual entries. The leaderboard doesn't count this one.",
  "{dur} on the clock, zero on the board — manual entries are off the record.",
  "Logged manually — respect for the honesty, but the points stay at zero.",
  "Training counts. Points don't — manual entries are out of the scoring.",
  "No Strava, no points. But {name} showed up anyway. That matters.",
  "Manual entry: the workout was real, the points are not. Nice try, {name}.",
  "Zero points for this one — but {name} gets full marks for effort.",
  "Off the grid, off the board. Manual entries don't score, but {name} keeps moving.",
  "Good discipline from {name} — too bad the scoring system doesn't reward the untracked.",
];

// ─── Activity name ────────────────────────────────────────────────────────────

const WITH_ACT_NAME: string[] = [
  '"{actName}" — the name says it all.',
  'Titled "{actName}" — the bar was set high. {name} cleared it.',
  '"{actName}" sounds innocent. The points don\'t lie.',
  'If you name your workout "{actName}", you mean business.',
  '"{actName}" — now that\'s a statement.',
  'The name: "{actName}". The result: earned.',
  '"{actName}" is now in my loss column.',
  'Creatively named, hard-earned: "{actName}".',
];

// ─── Challenge bonus ──────────────────────────────────────────────────────────

const WITH_CHALLENGE_BONUS: string[] = [
  "And on top of that, the challenge bonus. Unbelievable.",
  "Challenge completed, bonus collected. {name} doesn't do half measures.",
  "Extra points from the challenge bonus — like it's nothing.",
  "Casually picked up the challenge on the side. {name} is dangerous.",
  "Challenge bonus on top — as if the base points weren't enough.",
  "Complete the challenge, win twice. {name} knows the game.",
];

// ─── Heart rate bonus ─────────────────────────────────────────────────────────

const WITH_HR_BONUS: string[] = [
  "Even picked up the heart rate bonus — the heart was keeping score.",
  "HR bonus on top. {name} trains in the right zone.",
  "Full effort pays off: HR bonus claimed.",
  "The heart put in overtime today — and got rewarded for it.",
  "HR bonus? {name} doesn't train, {name} optimizes.",
  "Heart in the zone, bonus on the account. Nice.",
];

// ─── Early morning training < 7am ────────────────────────────────────────────

const TIME_EARLY: string[] = [
  "Before 7am. Seriously. Does {name} even sleep?",
  "The alarm clock is no match for {name}'s training schedule.",
  "While others are still dreaming, {name} is already collecting points.",
  "Early morning training — that's what I call a head start.",
  "The sun is barely up and {name} has already trained.",
  "Dawn training: the secret to {name}'s lead.",
];

// ─── Evening training 8–10pm ──────────────────────────────────────────────────

const TIME_EVENING: string[] = [
  "Training after 8pm — {name} has no excuses.",
  "After a long day, still {dur}. Respect, {name}.",
  "Most people are on the sofa. {name} is training.",
  "Evening training counts just the same. {name} knows this.",
  "After work? That's when {name}'s real training starts.",
];

// ─── Night training after 10pm ────────────────────────────────────────────────

const TIME_NIGHT: string[] = [
  "After 10pm and still on the track. {name} knows no 'too late'.",
  "Night training. Because there was no time earlier — or because {name} can.",
  "{name}'s day doesn't end until after the workout.",
  "Midnight points. The scoreboard never sleeps.",
];

// ─── Weekend training ─────────────────────────────────────────────────────────

const TIME_WEEKEND: string[] = [
  "Weekend? {name} trains anyway.",
  "Sunday is not a rest day — at least not for {name}.",
  "Saturday or Sunday makes no difference to {name}.",
  "While others take the weekend off, {name} keeps going.",
  "No days off. No points left on the table.",
];

// ─── Cycling speed km/h ───────────────────────────────────────────────────────

const WITH_SPEED: string[] = [
  "{speed} km/h average — that was no Sunday stroll.",
  "Average speed: {speed} km/h. Now that's pace.",
  "{speed} km/h over {dur} — the legs didn't let up.",
  "Moving at {speed} km/h. Respect, {name}.",
  "Average speed: {speed} km/h. Try to keep up with that.",
  "{speed} km/h — the front wheel gets light at that speed.",
  "Watts aside — {speed} km/h tells the whole story.",
  "Riding at {speed} km/h. {name} is not messing around.",
];

// ─── My weekly workout count ──────────────────────────────────────────────────

const WITH_MY_COUNT: string[] = [
  "I've only done {myCount} sessions this week — {name} just keeps adding more.",
  "{name} keeps piling on workouts while I'm sitting at {myCount} for the week.",
  "I'm at {myCount} workouts this week. {name} just keeps going.",
  "My {myCount} sessions this week look modest next to {name}'s consistency.",
  "{name} shows no signs of stopping. I'm at {myCount} workouts. That says it all.",
  "Me: {myCount} sessions this week. {name}: always training. Enough said.",
];

// ─── My total weekly training time ───────────────────────────────────────────

const WITH_MY_TOTAL_DUR: string[] = [
  "I've trained {myTotalDur} total this week — {name} nearly matches that in one session.",
  "{myTotalDur} of training for me this week. {name}'s single workout is eating into that.",
  "My entire week's training time: {myTotalDur}. This session alone is a big chunk of it.",
  "{name} packs in {dur} while I've only accumulated {myTotalDur} all week.",
  "Week total for me: {myTotalDur}. {name} is single-handedly threatening that number.",
  "My {myTotalDur} of weekly training vs. {name}'s relentless additions. Under pressure.",
];

// ─── Activity exceeds my longest single workout ───────────────────────────────

const ACTIVITY_LONGEST_WEEK: string[] = [
  "That {dur} would be my longest workout of the week. {name} doesn't flinch.",
  "My longest session this week was {myMaxDur}. {name} just did {dur}. Duly noted.",
  "{dur}? That beats my best session this week ({myMaxDur}). Respect.",
  "{name} logs {dur} — that would be my week's longest by far.",
  "My peak single session this week: {myMaxDur}. {name}'s today: {dur}. Bar raised.",
  "Outrunning my longest effort this week. {dur} vs. my {myMaxDur} peak.",
];

// ─── Sport variety ────────────────────────────────────────────────────────────

const WITH_SPORT_VARIETY: string[] = [
  "I'm doing {mySportCount} different sports this week — {name} still manages to outwork me.",
  "Already mixing it up with {mySportCount} sports this week and {name} still adds more.",
  "Variety is my thing this week ({mySportCount} sports) — but {name} keeps the pressure on.",
  "{mySportCount} different sports from me this week. {name}'s answer: more of everything.",
  "Training variety: {mySportCount} sports from me this week. {name} just goes harder.",
];

// ─── Final: loser speaks ──────────────────────────────────────────────────────

const FINAL_LOSER: string[] = [
  "Fair play — you were more consistent this week. This one's yours.",
  "Well played. I saw it, I accept it.",
  "You put in more. Acknowledged.",
  "This week goes to you. But the season is long.",
  "Clean win — I have nothing to complain about.",
  "No hard feelings, just respect. You were better this time.",
  "I wasn't worse — you were just better.",
  "The difference was real. I'll work on it.",
  "Congratulations. But don't get too comfortable.",
  "I won't forget this week's result.",
  "You outpaced me this week. Until next time.",
  "Had my chances. You had more. See you next week.",
  "Well deserved. Truly well deserved. I'll be back.",
  "The better athlete won. Today. Only today.",
  "You earned it. I'll earn it back.",
];

// ─── Final: draw ──────────────────────────────────────────────────────────────

const FINAL_DRAW: string[] = [
  "A draw? After all that? Shows how close this was.",
  "Equally strong, equally tired. Draw.",
  "We can't be separated. Next week decides.",
  "Level on points. The duel continues.",
  "Both gave everything — and still no winner. Respect.",
  "A draw is the most honest result between two madpeople.",
  "50:50. No difference. Everything open.",
  "We cancelled each other out. Fair enough.",
  "The draw feels like a shared victory.",
  "Level pegging. Until next week then.",
];

// ─── Public API ───────────────────────────────────────────────────────────────

export function generateActivityComment(
  act: Activity,
  subjectName: string,
  ctx?: SpeakerCtx,
): string {
  const durSecs   = act.duration_seconds ?? 0;
  const durMin    = durSecs / 60;
  const km        = (act.distance_meters && act.distance_meters >= 100) ? act.distance_meters / 1000 : null;
  const elev      = act.elevation_gain_m ?? null;
  const hr        = act.avg_heart_rate   ?? null;
  const pts       = act.total_points     ? Number(act.total_points) : null;
  const prCount   = act.pr_count         ?? 0;
  const isManual  = act.is_manual_entry && Number(act.is_manual_entry) > 0;
  const sport     = act.sport_type;
  const actName   = act.activity_name ?? '';
  const chalBonus = act.challenge_bonus_points ? Number(act.challenge_bonus_points) : 0;
  const hrBonus   = act.heart_rate_bonus_points ? Number(act.heart_rate_bonus_points) : 0;
  const startDate = act.started_at_utc ? new Date(act.started_at_utc.endsWith('Z') ? act.started_at_utc : act.started_at_utc + 'Z') : null;
  const startHour = startDate ? startDate.getHours() : -1;
  const startDay  = startDate ? startDate.getDay() : -1;
  const isWeekend = startDay === 0 || startDay === 6;

  const dur     = fmtDur(durSecs);
  const kmStr   = km   ? (km  >= 10 ? `${km.toFixed(1)} km`  : `${km.toFixed(2)} km`) : '';
  const elevStr = (elev !== null && Math.round(elev) > 0) ? `${Math.round(elev)} m`   : '';
  const hrStr   = hr   ? `${Math.round(hr)}`                                            : '';
  const ptsStr  = pts  ? `${Math.round(pts)}`                                           : '';

  const myKmStr       = ctx && ctx.totalKm > 0 ? (ctx.totalKm >= 10 ? `${ctx.totalKm.toFixed(1)} km` : `${ctx.totalKm.toFixed(2)} km`) : '';
  const myElevStr     = (ctx && Math.round(ctx.totalElev) > 0) ? `${Math.round(ctx.totalElev)} m` : '';
  const myPtsStr      = ctx && ctx.totalPoints > 0 ? `${Math.round(ctx.totalPoints)}` : '';
  const myCountStr    = ctx && ctx.workoutCount > 0 ? `${ctx.workoutCount}` : '';
  const myTotalDurStr = ctx && ctx.totalDurSecs > 0 ? fmtDur(ctx.totalDurSecs) : '';
  const myMaxDurStr   = ctx && ctx.maxDurSecs > 0 ? fmtDur(ctx.maxDurSecs) : '';
  const mySportCountStr = ctx && ctx.sportCount > 0 ? `${ctx.sportCount}` : '';

  let speed = '';
  if (km && km > 0 && durSecs > 0 && ['Ride', 'VirtualRide', 'EBikeRide'].includes(sport)) {
    speed = (km / (durSecs / 3600)).toFixed(1);
  }

  let pace = '';
  if (km && km > 0.5 && durSecs > 0 && ['Run', 'Walk', 'Hike'].includes(sport)) {
    const spk  = durSecs / km;
    const pMin = Math.floor(spk / 60);
    const pSec = Math.round(spk % 60);
    pace = `${pMin}:${String(pSec).padStart(2, '0')} min/km`;
  }

  // Sport-specific templates always first for activity relevance
  const pool: string[] = [];
  if (isManual) {
    pool.push(...SPORT_MANUAL);
  } else {
    switch (sport) {
      case 'Run':           pool.push(...SPORT_RUN);     break;
      case 'Ride':          pool.push(...SPORT_RIDE);    break;
      case 'Swim':          pool.push(...SPORT_SWIM);    break;
      case 'Hike':          pool.push(...SPORT_HIKE);    break;
      case 'Walk':          pool.push(...SPORT_WALK);    break;
      case 'WeightTraining':pool.push(...SPORT_WEIGHTS); break;
      case 'Yoga':          pool.push(...SPORT_YOGA);    break;
      case 'VirtualRide':   pool.push(...SPORT_VIRTUAL); break;
      default:              pool.push(...GENERIC);       break;
    }
  }

  // Duration
  if (durMin < 20)  pool.push(...DUR_SHORT);
  if (durMin > 75)  pool.push(...DUR_LONG);
  if (durMin > 120) pool.push(...DUR_VERY_LONG);

  // Distance
  if (kmStr) {
    pool.push(...WITH_KM);
    if (ctx && ctx.totalKm > 0) {
      if (km! >= ctx.totalKm)            pool.push(...KM_BEATS_OPP);
      else if (km! >= ctx.totalKm * 0.6) pool.push(...KM_NEAR_OPP);
    }
  }

  // Elevation (only if rounded value > 0)
  if (elevStr) {
    pool.push(...WITH_ELEV);
    if (ctx && Math.round(ctx.totalElev) > 0) {
      if (elev! >= ctx.totalElev)            pool.push(...ELEV_BEATS_OPP);
      else if (elev! >= ctx.totalElev * 0.5) pool.push(...ELEV_NEAR_OPP);
    }
  }

  // Heart rate
  if (hrStr) pool.push(...WITH_HR);

  // Points
  if (ptsStr) {
    if (pts! > 70) pool.push(...PTS_HIGH);
    if (pts! < 25) pool.push(...PTS_LOW);
    if (ctx && ctx.totalPoints > 0 && pts! > ctx.totalPoints * 0.3) pool.push(...PTS_BEATS_OPP);
  }

  // Pace
  if (pace) {
    const paceMin = parseInt(pace.split(':')[0]);
    if (paceMin < 5)  pool.push(...PACE_FAST);
    if (paceMin >= 7) pool.push(...PACE_SLOW);
  }

  // PRs
  if (prCount > 0) pool.push(...WITH_PR);

  // Activity name
  if (actName) pool.push(...WITH_ACT_NAME);

  // Bonus points
  if (chalBonus > 0) pool.push(...WITH_CHALLENGE_BONUS);
  if (hrBonus   > 0) pool.push(...WITH_HR_BONUS);

  // Time of day
  if      (startHour >= 0  && startHour < 7)  pool.push(...TIME_EARLY);
  else if (startHour >= 20 && startHour < 22) pool.push(...TIME_EVENING);
  else if (startHour >= 22)                   pool.push(...TIME_NIGHT);

  // Weekend
  if (isWeekend) pool.push(...TIME_WEEKEND);

  // Cycling speed
  if (speed) pool.push(...WITH_SPEED);

  // Comparative context
  if (myCountStr)         pool.push(...WITH_MY_COUNT);
  if (myTotalDurStr && ctx && ctx.totalDurSecs > 0 && durSecs > ctx.totalDurSecs * 0.4) pool.push(...WITH_MY_TOTAL_DUR);
  if (myMaxDurStr && ctx && durSecs > ctx.maxDurSecs) pool.push(...ACTIVITY_LONGEST_WEEK);
  if (mySportCountStr && ctx && ctx.sportCount >= 2)  pool.push(...WITH_SPORT_VARIETY);

  const template = pick(pool);
  return fill(template, {
    name:         subjectName,
    dur,
    km:           kmStr,
    elev:         elevStr,
    hr:           hrStr,
    pts:          ptsStr,
    pace,
    pr:           prCount,
    actName,
    speed,
    myKm:         myKmStr,
    myElev:       myElevStr,
    myPts:        myPtsStr,
    myCount:      myCountStr,
    myTotalDur:   myTotalDurStr,
    myMaxDur:     myMaxDurStr,
    mySportCount: mySportCountStr,
  });
}

export function generateFinalComment(winner: 'me' | 'opponent' | 'draw'): string {
  return pick(winner === 'draw' ? FINAL_DRAW : FINAL_LOSER);
}

export function generateAllComments(
  myActs: Activity[],
  oppActs: Activity[],
  myName: string,
  oppName: string,
): Record<string, string> {
  const myCtx  = computeCtx(myActs);
  const oppCtx = computeCtx(oppActs);
  const result: Record<string, string> = {};
  for (const a of myActs)  result[String(a.id)] = generateActivityComment(a, myName, oppCtx);
  for (const a of oppActs) result[String(a.id)] = generateActivityComment(a, oppName, myCtx);
  return result;
}
